import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grow,
  useMediaQuery,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getActivities, deleteActivity } from '../services/api/activity';
import { useTheme } from '../context/ThemeContext';

const Transition = React.forwardRef((props, ref) => {
  return <Grow ref={ref} {...props} />;
});

const Activities = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [activitiesData, setActivitiesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState(null);
  const navigate = useNavigate();
  const { actualTheme } = useTheme();

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const data = await getActivities();
        setActivitiesData(data.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, []);

  const getChipColor = (type) => {
    switch (type) {
      case 'File':
        return 'primary';
      default:
        return 'default';
    }
  };

  const handleRowClick = (id) => {
    navigate(`/activity/${id}`);
  };

  const handleOpenDeleteDialog = (event, id) => {
    event.stopPropagation();
    setActivityToDelete(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setActivityToDelete(null);
  };

  const handleDelete = async () => {
    try {
      await deleteActivity(activityToDelete);
      setActivitiesData(activitiesData.filter((activity) => activity.id !== activityToDelete));
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error deleting activity:', error);
      handleCloseDeleteDialog();
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 12, mb: 4 }}>
      <Typography
        variant="h4"
        component="h1"
        sx={{
          mb: 4,
          fontWeight: 300,
          letterSpacing: '0.1em',
        }}
      >
        {t('myActivities.title')}
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            border: '1px solid rgba(255, 255, 255, 0.1)', // Fixed border color
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('myActivities.name')}</TableCell>
                {!isMobile && (
                  <TableCell>{t('myActivities.type')}</TableCell>
                )}
                <TableCell>{t('myActivities.date')}</TableCell>
                {!isMobile && (
                  <TableCell>{t('myActivities.description')}</TableCell>
                )}
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activitiesData.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                    cursor: 'pointer',
                  }}
                  onClick={() => handleRowClick(row.id)}
                >
                  <TableCell component="th" scope="row">
                    {row.name}
                  </TableCell>
                  
                {!isMobile && (
                  <TableCell>
                    <Chip
                      label="File"
                      color={getChipColor('File')}
                      size="small"
                      sx={{ fontWeight: 300 }}
                    />
                  </TableCell>
                )}
                  
                  <TableCell>{new Date(row.date).toLocaleString()}</TableCell>
                  
                {!isMobile && (
                  <TableCell>
                    {row.description.length > 15
                      ? `${row.description.slice(0, 24)}...`
                      : row.description}
                  </TableCell>
                )}
                  <TableCell align="right">
                    <Tooltip title={t('myActivities.delete')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(event) => handleOpenDeleteDialog(event, row.id)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Transition}
        TransitionProps={{
          timeout: {
            enter: 400,
            exit: 300,
          },
        }}
        PaperProps={{
          sx: {
            background: (theme) =>
              theme.palette.mode === 'light'
                ? 'rgba(255, 255, 255, 0.9)'
                : 'rgba(24, 24, 28, 0.98)',
            backdropFilter: 'blur(10px)',
            border: (theme) =>
              theme.palette.mode === 'light'
                ? '1px solid rgba(0, 0, 0, 0.1)'
                : '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            transition: 'all 0.3s ease-in-out',
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              backdropFilter: 'blur(4px)',
              transition: 'opacity 0.3s ease-in-out',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            pt: 3,
            textAlign: 'center',
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 300,
              color: 'text.primary',
            }}
          >
            {t('myActivities.confirmDeleteTitle')}
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            pt: 2,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mt: 2,
            }}
          >
            {t('myActivities.confirmDeleteMessage')}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'center',
            pb: 3,
          }}
        >
          <Button
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            color="inherit"
            sx={{
              textTransform: 'none',
              mr: 1,
            }}
          >
            {t('myActivities.cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            sx={{
              textTransform: 'none',
            }}
          >
            {t('myActivities.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Activities;